/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./MinimoveHome.css";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import { useAppContext } from "../../../../AppContext";
import {
  getEventScope,
  isCurrentScope,
  newGuestId,
  setActiveGuestId,
  storeGuestIdentity,
} from "../../../../eventIdentity";
import {
  openDatabase,
  getLastUpdateTime,
  updateLastUpdateTime,
  cleanUpOldEntries,
  saveImageBlobToDB,
  getImageBlobFromDB,
  getAllKeysFromDB,
  deleteSelectedImages,
} from "../../../../IndexedDBUtils";
// import homePageGif from "../../../../assets/gifs/HomePageGif.gif";
import headerImage from "../../../../assets/images/Minimove-Header.png";
import { base64ToBlob } from "../../../../generalUtils";
import { calculateFaceStyle } from "../../../../Components/Common/FacePositionUtils/faceUtils";
import { SERVER_URL } from "../../../../config/serverUrl";
import { getFaceClaimEvent } from "../../shared/faceClaimEvent";
import { releaseStoredClaim } from "../../shared/faceClaimClient";

const STATIC_BODY_URL = "/HomeMiniMoveBody.png";
const STATIC_FACE_URL = "/HomeMiniMoveFace.png";

const FACE_HEIGHT_RATIO = 0.788;
const FACE_CENTER_X = 0.51;
const FACE_CENTER_Y = 0.494;

const FACE_ANCHOR_PX = { x: 302, y: 40 }; // Face position in image

const FACE_STORE_NAME = "FaceImages";
const BODY_STORE_NAME = "BodyImages";
const FACE_REFRESH_INTERVAL_SECONDS = 20;
const BODY_REFRESH_INTERVAL_SECONDS = 20;

// Latest body-upload operation across Home visits (see uploads_bodies below).
let bodiesUploadOp = 0;

const FACE_TTL = 1 * 1 * 1.5 * 60 * 1000; // 90 seconds
const BODY_TTL = 1 * 1 * 2 * 60 * 1000; // 2 Minutes

const MinimoveHomePage = ({ reFace }) => {
  const navigate = useNavigate();
  const { showLoading, hideLoading, acquireLoading } = useLoading();
  // eventName is set by EventRouteGate (validated route name), never here.
  const { eventName, setUserId } = useAppContext();
  const serverUrl = SERVER_URL;
  const businessSiteUrl = process.env.REACT_APP_BUSINESS_SITE_URL;
  const [timeLeft, setTimeLeft] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [graceMinutes, setGraceMinutes] = useState(0);
  // Right after existing useState:
  const bodyImgRef = useRef(null);
  const previewBoxRef = useRef(null);

  const [selectedFace, setSelectedFace] = useState(null); // Face data
  const [faceStyle, setFaceStyle] = useState(null); // Computed style
  const [imgReady, setImgReady] = useState(false); // Both images loaded

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setSelectedFace({
        url: STATIC_FACE_URL,
        faceHeightRatio: FACE_HEIGHT_RATIO,
        centerX: FACE_CENTER_X,
        centerY: FACE_CENTER_Y,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      });
    };
    img.src = STATIC_FACE_URL;
  }, []);

  const handleBodyLoad = () => {
    if (
      bodyImgRef.current &&
      bodyImgRef.current.complete &&
      bodyImgRef.current.naturalWidth > 0
    ) {
      setImgReady(true);
    } else {
      setTimeout(() => handleBodyLoad(), 50);
    }
  };

  useEffect(() => {
    if (
      !imgReady ||
      !selectedFace ||
      !bodyImgRef.current ||
      !previewBoxRef.current
    )
      return;

    const recalc = () => {
      const imgEl = bodyImgRef.current;
      const natural = { w: imgEl.naturalWidth, h: imgEl.naturalHeight };

      const bodyData = {
        face_position_ratio: {
          x: FACE_ANCHOR_PX.x / natural.w,
          y: FACE_ANCHOR_PX.y / natural.h,
        },
      };

      const style = calculateFaceStyle(
        imgEl,
        previewBoxRef.current,
        bodyData,
        selectedFace,
        true,
        { name: "headSwing", duration: "1.5s", timing: "ease-in-out" },
        0
      );
      if (style) style.transformOrigin = "50% 100%";
      setFaceStyle(style);
    };

    recalc(); // Initial calculation

    // Additional calculations after delay
    const timerIds = [
      setTimeout(recalc, 50), // Attempt after 50ms
      setTimeout(recalc, 100), // Attempt after 100ms
      setTimeout(recalc, 300), // Attempt after 300ms
    ];

    window.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("resize", recalc);
      timerIds.forEach((id) => clearTimeout(id));
    };
  }, [imgReady, selectedFace]);

  const parseDate = (dateStr) => {
    // Assuming the format is "DD/MM/YYYY HH:mm"
    const [datePart, timePart] = dateStr.split(" ");
    const [day, month, year] = datePart.split("/");
    return new Date(`${year}-${month}-${day}T${timePart}:00`);
  };

  const calculateTimeLeft = (t) => {
    const parsedDate = parseDate(t);
    const difference = new Date(parsedDate) - new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return timeLeft;
  };

  // `isCurrent` turns false once this page was unmounted or the event scope
  // changed; the work then stops and writes no shared storage or freshness
  // timestamp. IDB writes already in flight only target this event's database.
  // Resolves to { writtenFrom } (every entry this batch wrote is stamped at or
  // after it) once every item of the batch was stored; otherwise (skipped by
  // the refresh guard, failed, partly stored or cancelled) to undefined.
  const fetchAndStoreImagesBatch = async (
    folderType,
    storeName,
    ttl,
    isCurrent
  ) => {
    try {
      const lastUpdateTime = await getLastUpdateTime(eventName, storeName);
      if (!isCurrent()) return;
      const currentTime = new Date();
      const timeDifference = (currentTime - new Date(lastUpdateTime)) / 1000;

      if (timeDifference < ttl) {
        return;
      }
      const response = await fetch(
        `${serverUrl}/api/images/batch?folder_path=${encodeURIComponent(
          `${eventName}/${folderType}`
        )}`
      );
      if (!isCurrent()) return;

      if (!response.ok) {
        throw new Error(
          `Failed to fetch ${folderType} images batch: ${response.status}`
        );
      }

      const imageBatch = await response.json();
      if (!isCurrent()) return;
      const serverPaths = Object.keys(imageBatch);
      let successCount = 0;

      // Get current paths in IndexedDB to find deleted images
      const keysFromDB = await getAllKeysFromDB(eventName, storeName);
      if (!isCurrent()) return;
      const pathsToDelete = keysFromDB.filter(
        (path) => !serverPaths.includes(path)
      );

      // Delete images that no longer exist on server
      if (pathsToDelete.length > 0) {
        await deleteSelectedImages(eventName, storeName, pathsToDelete);
      }

      const writtenFrom = new Date().toISOString();
      // Transitioning to consistent use of Blob instead of base64
      for (const [path, fileData] of Object.entries(imageBatch)) {
        if (!isCurrent()) return;
        try {
          const { base64, width, height, face_position_ratio } = fileData;

          // Converting base64 to Blob in a controlled manner
          const imageBlob = await base64ToBlob(base64, "image/webp");
          // A decode that outlived this visit must not START a new write:
          // after A -> B -> A the same-named database belongs to the newer one.
          if (!isCurrent()) return;

          // Saving to database
          await saveImageBlobToDB(
            eventName,
            storeName,
            path,
            imageBlob,
            width,
            height,
            face_position_ratio
          );

          // Checking that the image was saved successfully
          const verifyBlob = await getImageBlobFromDB(
            eventName,
            storeName,
            path
          );
          if (!verifyBlob) {
            throw new Error(`Verification failed for ${path}`);
          }

          successCount++;
        } catch (itemError) {
          console.error(`Failed to process item ${path}:`, itemError);
          // Continue to the next item instead of stopping the entire process
        }
      }

      if (!isCurrent()) return;
      if (successCount > 0) {
        await updateLastUpdateTime(eventName, storeName);
      } else {
        console.warn(`No images were successfully saved for ${folderType}`);
      }
      if (successCount === serverPaths.length) return { writtenFrom };
    } catch (error) {
      console.error(`Error fetching and storing ${folderType} images:`, error);
    }
  };

  // The TTL purge only follows a batch that stored its folder completely, and
  // never touches what that batch wrote (keepFrom). Run before the batch, it
  // emptied the store whenever the 20 s refresh guard skipped the refetch (the
  // galleries refresh the guard's timestamp on every no-change check without
  // re-stamping entries) or the batch failed, and the next gallery open
  // downloaded everything again (2026-10-06). After a complete batch it is a
  // safety net only: the batch already dropped what left the server and
  // re-stamped the rest.
  const purgeAfterBatch = async (batch, storeName, ttl, isCurrent) => {
    if (!batch || !isCurrent()) return;
    await cleanUpOldEntries(eventName, storeName, ttl, {
      keepFrom: batch.writtenFrom,
    });
  };

  const headerImgRef = useRef(null);

  useEffect(() => {
    const setOffsets = () => {
      const header = document.querySelector(".minimove-home-title");
      const footer = document.querySelector(".minimove-home-footer");
      if (header)
        document.documentElement.style.setProperty(
          "--headerH",
          header.offsetHeight + "px"
        );
      if (footer)
        document.documentElement.style.setProperty(
          "--footerH",
          footer.offsetHeight + "px"
        );
    };

    window.addEventListener("load", setOffsets, { once: true });
    window.addEventListener("resize", setOffsets);
    if (headerImgRef.current && !headerImgRef.current.complete) {
      headerImgRef.current.addEventListener("load", setOffsets, { once: true });
    }

    const bodyOnLoad = () => setOffsets();
    bodyImgRef.current?.addEventListener("load", bodyOnLoad, { once: true });
    return () => {
      window.removeEventListener("resize", setOffsets);
      window.removeEventListener("load", setOffsets);
      bodyImgRef.current?.removeEventListener("load", bodyOnLoad);
    };
  }, []);

  useEffect(() => {
    if (!eventName) return undefined;
    const scope = getEventScope();
    let cancelled = false;
    const isCurrent = () => !cancelled && isCurrentScope(scope);
    const releaseLoading = acquireLoading();

    const initializeAndLoadData = async () => {
      await openDatabase(eventName);
      if (!isCurrent()) return;

      try {
        const response = await fetch(
          `${serverUrl}/api/get-end_event_time?event_name=${encodeURIComponent(
            eventName
          )}`
        );
        const data = await response.json();
        if (!isCurrent()) return;
        if (data.endTime) {
          setEndTime(data.endTime);
          setGraceMinutes(Number(data.graceMinutes) || 0);
          setTimeLeft(calculateTimeLeft(data.endTime));
        }
      } catch (error) {
        console.error("Error getting event end time:", error);
      }
      if (!isCurrent()) return;

      // A previous guest's editing hold is released (best effort) BEFORE the legacy fresh-guest clear
      // below wipes its handle; the server lease backstops a lost request.
      releaseStoredClaim(getFaceClaimEvent());
      sessionStorage.clear();
      localStorage.clear();

      // Each Home visit starts a new guest (shared generator with the gate).
      // Register and context are set together (the claim client reads the register, the approve
      // the context); the guarded storage write is only the reload carrier.
      const uuid = newGuestId();
      storeGuestIdentity(eventName, uuid);
      setActiveGuestId(uuid);
      sessionStorage.setItem("reFace", reFace);
      setUserId(uuid);

      const faceBatch = await fetchAndStoreImagesBatch(
        "faces/ready",
        FACE_STORE_NAME,
        FACE_REFRESH_INTERVAL_SECONDS,
        isCurrent
      );
      if (!isCurrent()) return;
      await purgeAfterBatch(faceBatch, FACE_STORE_NAME, FACE_TTL, isCurrent);
      if (!isCurrent()) return;
      hideLoading();
      releaseLoading();
      // The flag belongs to this operation: it is cleared when THIS body batch
      // ends, even if the page unmounted for a same-event page change, but
      // never after another event/visit took over or a newer Home started its
      // own batch (bodiesUploadOp moved on).
      const uploadOp = ++bodiesUploadOp;
      sessionStorage.setItem("uploads_bodies", true);
      try {
        const bodyBatch = await fetchAndStoreImagesBatch(
          "bodies",
          BODY_STORE_NAME,
          BODY_REFRESH_INTERVAL_SECONDS,
          isCurrent
        );
        // MiniMove's body gallery has no server fallback: a purge before a
        // skipped or failed batch left it empty.
        await purgeAfterBatch(bodyBatch, BODY_STORE_NAME, BODY_TTL, isCurrent);
      } finally {
        if (uploadOp === bodiesUploadOp && isCurrentScope(scope)) {
          sessionStorage.setItem("uploads_bodies", false);
        }
      }
    };
    // Any failure (including before the inner try, e.g. openDatabase or
    // cleanUpOldEntries rejecting) is reported once and always releases THIS
    // owner's loading token; other owners' tokens are untouched.
    initializeAndLoadData()
      .catch((error) => {
        console.error("Error initializing event data:", error);
      })
      .finally(() => {
        releaseLoading();
      });

    return () => {
      cancelled = true;
      releaseLoading();
    };
  }, [eventName, serverUrl]);

  useEffect(() => {
    if (endTime) {
      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft(endTime));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [endTime]);

  const formatTimeLeft = () => {
    if (!timeLeft) return "00:00:00";
    const days = timeLeft.days > 0 ? (timeLeft.days === 1 ? `יום אחד ` : `${timeLeft.days} ימים `) : "";
    return `${days}${String(timeLeft.hours).padStart(2, "0")}:${String(
      timeLeft.minutes
    ).padStart(2, "0")}:${String(timeLeft.seconds).padStart(2, "0")}`;
  };


  // Universal end grace (2026-08-24): past the scheduled end the event stays
  // live for graceMinutes server-side. The timer used to just vanish; now the
  // guest sees "האירוע הסתיים" — blinking through the grace window (photos
  // still go through), steady once it truly closed.
  const endedPhase = () => {
    if (!endTime) return null;
    const end = parseDate(endTime);
    if (!end || Number.isNaN(end.getTime())) return null;
    const now = Date.now();
    if (now < end.getTime()) return null;
    const graceMs = (graceMinutes || 0) * 60 * 1000;
    return now < end.getTime() + graceMs ? "grace" : "over";
  };

  const goToNextPage = () => {
    showLoading();
    navigate(`/${eventName}/face-gallery`);
  };

  return (
    <div className="minimove-home-container">
      <div className="minimove-home-title">
        {businessSiteUrl ? (
          <a
            href={businessSiteUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              ref={headerImgRef}
              src={headerImage}
              alt="MiniMove"
              className="minimove-home-header-image"
            />
          </a>
        ) : (
          <img
            ref={headerImgRef}
            src={headerImage}
            alt="MiniMove"
            className="minimove-home-header-image"
          />
        )}
      </div>

      <div className="minimove-home-gif-container" ref={previewBoxRef}>
        <img
          ref={bodyImgRef}
          src={STATIC_BODY_URL}
          alt="MiniMove Body"
          onLoad={handleBodyLoad}
        />
        {selectedFace && faceStyle && (
          <div className="face-overlay animated-face" style={faceStyle} />
        )}
      </div>

      <div className="minimove-home-footer">
        {endTime && timeLeft && timeLeft.hours !== undefined && (
          <div className="minimove-home-remaining-text rtl">
            נותרו {formatTimeLeft()} לעיצוב מינימוב משלך.
          </div>
        )}
        {endedPhase() && (
          <div
            className={`minimove-home-remaining-text minimove-home-ended rtl${
              endedPhase() === "grace" ? " minimove-home-ended-blink" : ""
            }`}
          >
            האירוע הסתיים
          </div>
        )}

        <div className="minimove-home-start-button-container">
          <button className="minimove-home-start-button" onClick={goToNextPage}>
            התחל
          </button>
        </div>
      </div>
    </div>
  );
};

export default MinimoveHomePage;
